export interface SignificantDate {
  date: number;
  notes: string;
}

export interface Address {
  country: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  poBox: string;
  label: string;
}

export interface Contact {
  id?: string;
  labelIds?: number[] | null;
  firstName?: string;
  lastName?: string;
  prefix?: string;
  middleName?: string;
  suffix?: string;
  phoneticFirst?: string;
  phoneticMiddle?: string;
  phoneticLast?: string;
  nickname?: string;
  fileAs?: string;
  emails?: ValueLabel[];
  phoneNumbers?: ValueLabel[];
  companyName?: string;
  companyDepartment?: string;
  companyJobTitle?: string;
  addresses?: Address[];
  birthday?: number;
  significantDates?: SignificantDate[];
  websites?: ValueLabel[];
  relatedPeople?: ValueLabel[];
  customFields?: ValueLabel[];
  notes?: string;
  trashedTime?: number;
  createdBy?: string;
  createdTime?: number;
  updatedBy?: string;
  updatedTime?: number;
  activeLabels?: string[];
}

export interface ValueLabel {
  value: string;
  label?: string;
}

export const recommendedLabel = {
  email: ['Home', 'Work', 'Other'],
  phone: [
    'Home',
    'Work',
    'Other',
    'Mobile',
    'Main',
    'Home Fax',
    'Work Fax',
    'Google Voice',
    'Pager',
  ],
  address: ['Home', 'Work', 'Other'],
  significantDate: ['Anniversary', 'Other'],
  website: ['Profile', 'Blog', 'Home Page', 'Work'],
  relatedPerson: [
    'Spouse',
    'Child',
    'Mother',
    'Father',
    'Parent',
    'Brother',
    'Sister',
    'Friend',
    'Relative',
    'Manager',
    'Assistant',
    'Reference',
    'Partner',
    'Domestic Partner',
  ],
} as const;

export type ContactParam = Omit<Contact, 'birthday' | 'significantDates'> & {
  birthday: number | undefined;
  significantDates: {
    date: number | undefined;
    notes: string;
  }[];
};

export interface UserSearchShare {
  firstName: string;
  lastName: string;
  userEmail: string;
  suspended: boolean;
  archived: boolean;
  orgUnitPath: string;
  permission: string;
  domain: string;
}

export interface ShareUser {
  firstName: string;
  lastName: string;
  userEmail: string;
  permission: string;
}

export interface ContactsShareRequest {
  acl: ShareUser[];
  notify: boolean;
  notifyMessage: string;
  generalAccess: string;
}

export interface Label {
  id: number;
  name: string;
  createdTime: number;
  updatedTime: number;
  creatorEmail: string;
  trashedTime: number;
  deletedTime: number;
  count: number | null;
  trashed: boolean | null;
  syncRunning: boolean | null;
}

export interface LabelGroup {
  labels: Label[];
}

export interface LabelGroupsResponse {
  myContactsCount: number;
  organizationDirectoryCount: number;
  contactsSyncsCount: number;
  labelGroups: LabelGroup[];
  trashedContactsCount: number;
  trashedLabelsCount: number;
}

export interface LabelMutationResponse {
  updated: Label[];
  group: LabelGroupsResponse;
}

export type EnumValues<E> = E[keyof E];

export interface GPanelContactsLabelsLogsResponse {
  cursor: string;
  results: GPanelContactsLabelsLogs[];
  firstPull: boolean;
  searchTime: number;
  pageSize: number;
}

export interface GPanelContactsLabelsLogs {
  createdDate: string;
  performingUser: string;
  action: string;
  changeLogReason: string;
}

export interface GPanelContactsDeletePageProps {
  handleClose: () => void;
  contactIds: number[];
}

export interface GPanelContactsImportRequest {
  userEmail: string;
  sourceLabel: string;
  targetLabel: number | null;
  sendNotification: boolean;
  newLabelName: string;
}

export interface GPanelContactsAddToLabelDialogProps {
  handleClose: () => void;
  contactIds: number[];
  contacts?: Contact[];
  selectedLabelIds?: number[];
  anchorEl: HTMLElement | null;
}

export interface GPanelContactsAddToLabelsRequest {
  labelIds: number[] | [];
  contactIds: number[] | [];
  changeLogMessage?: string;
}

export type TargetType = 'User' | 'Group' | 'Domain' | 'OrgUnit';

export interface LabelSync {
  id: number;
  createdTime: number;
  createdBy: string;
  updatedTime: number;
  updatedBy: string;
  name: string;
  description?: string;
  sourceLabel: string;
  targetLabelName: string;
  targetType: TargetType;
  targets: string[];
  labelId: number;
  addToMyContacts: boolean;
  allSource: boolean;
  status: string;
}

export interface LabelSyncUserStatus {
  id: number;
  syncId: number;
  userEmail: string;
  error: string | null;
  createdTime: number;
  updatedTime: number;
}
